/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: phonebooksApi.ts
 🗓️ Created: 2025-12-05
 🧩 Description:
    API client for Phonebooks module
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type {
  PhonebooksListResponse,
  Phonebook,
  PhonebookFormData,
  PhonebookEntry,
  CSVContact,
  ImportResult,
} from '../types';

/**
 * Fetch phonebooks list
 */
export async function fetchPhonebooks(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  type?: 'internal' | 'external' | 'all';
  domainId?: number;
}): Promise<PhonebooksListResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  if (params.search) {
    queryParams.append('search', params.search);
  }

  if (params.type) {
    queryParams.append('type', params.type);
  }

  if (params.domainId != null && !Number.isNaN(Number(params.domainId))) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/phonebooks?${queryParams}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch phonebooks');
  }

  return response.json();
}

/**
 * Fetch phonebook by ID
 */
export async function fetchPhonebookById(id: string | number): Promise<Phonebook> {
  const response = await fetch(
    buildApiUrl(`/api/phonebooks/${id}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch phonebook');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Create phonebook
 */
export async function createPhonebook(data: PhonebookFormData): Promise<Phonebook> {
  const response = await fetch(
    buildApiUrl('/api/phonebooks'),
    {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create phonebook');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Update phonebook
 */
export async function updatePhonebook(
  id: string | number,
  data: Partial<PhonebookFormData>
): Promise<Phonebook> {
  const response = await fetch(
    buildApiUrl(`/api/phonebooks/${id}`),
    {
      method: 'PUT',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update phonebook');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Delete phonebook
 */
export async function deletePhonebook(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/phonebooks/${id}`),
    {
      method: 'DELETE',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete phonebook');
  }
}

/**
 * Fetch phonebook entries (internal or external contacts)
 */
export async function fetchPhonebookEntries(phonebookId: string | number): Promise<PhonebookEntry[]> {
  const response = await fetch(
    buildApiUrl(`/api/phonebooks/${phonebookId}/entries`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch phonebook entries');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Import contacts from CSV
 */
export async function importContacts(
  phonebookId: string | number,
  contacts: CSVContact[]
): Promise<ImportResult> {
  const response = await fetch(
    buildApiUrl(`/api/phonebooks/${phonebookId}/contacts/import`),
    {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ contacts }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to import contacts');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Export phonebook contacts to CSV
 */
export async function exportPhonebookContacts(phonebookId: string | number): Promise<Blob> {
  const response = await fetch(
    buildApiUrl(`/api/phonebooks/${phonebookId}/contacts/export`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to export phonebook contacts');
  }

  return response.blob();
}

/**
 * Interface for selectable items in multi-select components
 */
export interface SelectableItem {
  id: string;
  name: string;
  description?: string;
  slug?: string;
}

/**
 * Fetch extensions for phonebook selection
 */
export async function fetchExtensionsForPhonebook(): Promise<SelectableItem[]> {
  const response = await fetch(
    buildApiUrl('/api/telephony/extensions?limit=1000&sortBy=extension&sortOrder=asc'),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    return [];
  }

  const result = await response.json();
  if (result.success && Array.isArray(result.data)) {
    return result.data.map((ext: { id?: unknown; extension: string; name?: string }) => ({
      id: ext.id?.toString() || ext.extension,
      name: `${ext.extension}${ext.name ? ` - ${ext.name}` : ''}`,
      description: ext.name || ext.extension,
      slug: ext.extension,
    }));
  }

  return [];
}

/**
 * Fetch queues for phonebook selection
 */
export async function fetchQueuesForPhonebook(): Promise<SelectableItem[]> {
  const response = await fetch(
    buildApiUrl('/api/telephony/queues?limit=1000&sortBy=name&sortOrder=asc'),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    return [];
  }

  const result = await response.json();
  if (result.success && Array.isArray(result.data)) {
    return result.data.map((queue: { id?: unknown; name?: string; extension?: string }) => ({
      id: queue.id?.toString(),
      name: `${queue.extension || queue.name}${queue.name && queue.extension !== queue.name ? ` - ${queue.name}` : ''}`,
      description: queue.name || queue.extension,
      slug: queue.extension || queue.name,
    }));
  }

  return [];
}

/**
 * Fetch ring groups for phonebook selection
 */
export async function fetchRingGroupsForPhonebook(): Promise<SelectableItem[]> {
  const response = await fetch(
    buildApiUrl('/api/telephony/ring-groups?limit=1000&sortBy=name&sortOrder=asc'),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    return [];
  }

  const result = await response.json();
  if (result.success && Array.isArray(result.data)) {
    return result.data.map((rg: { id?: unknown; name: string; extension?: string; context?: string }) => ({
      id: rg.id?.toString(),
      name: `${rg.name}${rg.extension ? ` (${rg.extension})` : ''}`,
      description: rg.name,
      slug: rg.context || rg.name,
    }));
  }

  return [];
}

/**
 * Fetch conferences for phonebook selection
 */
export async function fetchConferencesForPhonebook(): Promise<SelectableItem[]> {
  const response = await fetch(
    buildApiUrl('/api/telephony/conferences?limit=1000&sortBy=extension&sortOrder=asc'),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    return [];
  }

  const result = await response.json();
  if (result.success && Array.isArray(result.data)) {
    return result.data.map((conf: { id?: unknown; extension: string; name?: string }) => ({
      id: conf.id?.toString(),
      name: `${conf.extension}${conf.name ? ` - ${conf.name}` : ''}`,
      description: conf.name || conf.extension,
      slug: conf.extension,
    }));
  }

  return [];
}

/**
 * Fetch feature codes for phonebook selection
 */
export async function fetchFeatureCodesForPhonebook(): Promise<SelectableItem[]> {
  const response = await fetch(
    buildApiUrl('/api/telephony/feature-codes?limit=1000&sortBy=name&sortOrder=asc'),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    return [];
  }

  const result = await response.json();
  if (result.success && Array.isArray(result.data)) {
    return result.data.map((fc: { id?: unknown; customNumber?: string; defaultNumber?: string; name?: string; description?: string }) => ({
      id: fc.id?.toString(),
      name: `${fc.customNumber || fc.defaultNumber}${fc.description ? ` - ${fc.description}` : ` - ${fc.name}`}`,
      description: fc.description || fc.name,
      slug: fc.customNumber || fc.defaultNumber,
    }));
  }

  return [];
}

/**
 * Fetch speed dials for phonebook selection
 */
export async function fetchSpeedDialsForPhonebook(): Promise<SelectableItem[]> {
  const response = await fetch(
    buildApiUrl('/api/telephony/speed-dials?limit=1000&sortBy=speedCode&sortOrder=asc'),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    return [];
  }

  const result = await response.json();
  if (result.success && Array.isArray(result.data)) {
    return result.data.map((sd: { id?: unknown; speedCode: string; description?: string; destination: string }) => ({
      id: sd.id?.toString(),
      name: `${sd.speedCode}${sd.description ? ` - ${sd.description}` : ` → ${sd.destination}`}`,
      description: sd.description || sd.destination,
      slug: sd.speedCode,
    }));
  }

  return [];
}
